import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Store, ShoppingBag, Sparkles, TrendingUp, Palette } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

export default function Landing() {
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = 'Fluencer - Create Your Influencer Store';
  }, []);
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border backdrop-blur-sm bg-background/80 sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Store className="w-6 h-6 text-primary" />
            <span className="text-xl font-outfit font-bold">Fluencer.shop</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="header-login-button"
              variant="ghost"
              className="rounded-full"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
            <Button
              data-testid="header-signup-button"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-bold"
              onClick={() => navigate('/register')}
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background"></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Create Your Influencer Store</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-outfit font-bold mb-6 tracking-tight">
              Curate. Recommend.
              <br />
              <span className="text-primary">Earn.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Build beautiful stores to showcase your favorite products. Share your recommendations and earn through affiliate links—no cart, no checkout, just pure curation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="hero-get-started-button"
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-bold h-12 px-8"
                onClick={() => navigate('/register')}
              >
                Start Building Free
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 bg-card/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-outfit font-bold mb-4">Everything You Need</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Powerful tools designed for influencers, creators, and curators
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-xl p-8 hover:border-primary/50 transition-colors"
            >
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                <ShoppingBag className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-outfit font-bold mb-3">Product Curation</h3>
              <p className="text-muted-foreground leading-relaxed">
                Import products from Amazon, Walmart, and more. Add your recommendations and personal touch to every item.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-xl p-8 hover:border-primary/50 transition-colors"
            >
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                <Palette className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-outfit font-bold mb-3">Beautiful Themes</h3>
              <p className="text-muted-foreground leading-relaxed">
                Choose from stunning pre-built themes or customize every detail. Make your store uniquely yours.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-xl p-8 hover:border-primary/50 transition-colors"
            >
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-outfit font-bold mb-3">Earn & Grow</h3>
              <p className="text-muted-foreground leading-relaxed">
                Use your affiliate links to earn commissions. Track your performance and grow your influence.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-12 border border-primary/20"
          >
            <h2 className="text-3xl md:text-4xl font-outfit font-bold mb-4">
              Ready to Start Curating?
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Join thousands of creators building their influencer stores
            </p>
            <Button
              data-testid="cta-get-started-button"
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-bold h-12 px-8"
              onClick={() => navigate('/register')}
            >
              Create Your Store
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2025 Fluencer.shop. Built for creators, by creators.</p>
        </div>
      </footer>
    </div>
  );
}